import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

// Static recharts style config — hoisted to module scope so these object/array
// literals aren't re-created on every render.
const X_AXIS_TICK = { fill: '#66707A', fontSize: 11 };
const X_AXIS_LINE = { stroke: '#DDE1E5' };
const Y_AXIS_TICK = { fill: '#8A929B', fontSize: 10 };
const BAR_RADIUS = [3, 3, 0, 0];

/**
 * Side-by-side bar chart comparing user's effort/duration estimate to industry
 * averages.
 */
export function BenchmarkChart({ comparison }) {
  const { t } = useTranslation();

  const chartData = [
    { name: t('benchmark.effortDays'), yours: comparison.effort.yours, average: comparison.effort.avg },
    { name: t('benchmark.durationWks'), yours: comparison.duration.yours, average: comparison.duration.avg },
  ];

  return (
    <Card className="bg-white border-[#DDE1E5]">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-[#66707A] uppercase tracking-wider">
          {t('benchmark.visualComparison')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48" data-testid="benchmark-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              barCategoryGap="30%"
              title={t('benchmark.visualComparison')}
              desc={chartData.map(d => `${d.name}: ${d.yours} / ${d.average}`).join(', ')}
            >
              <XAxis dataKey="name" tick={X_AXIS_TICK} axisLine={X_AXIS_LINE} tickLine={false} />
              <YAxis tick={Y_AXIS_TICK} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="bg-white border border-[#DDE1E5] rounded-lg px-3 py-2 shadow-card">
                      <div className="text-xs text-[#66707A] mb-1">{label}</div>
                      {payload.map((p) => (
                        <div key={p.dataKey} className="flex items-center gap-2 text-xs">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.fill }} />
                          <span className="text-[#171A1D]">
                            {p.dataKey === 'yours' ? t('benchmark.yourEstimate') : t('benchmark.industryAvg')}:{' '}
                          </span>
                          <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: p.fill }}>{p.value}</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Bar dataKey="yours" name={t('benchmark.yourEstimate')} fill="#78B928" radius={BAR_RADIUS} />
              <Bar dataKey="average" name={t('benchmark.industryAvg')} fill="#DDE1E5" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-6 mt-2 text-xs">
          <span className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm bg-[#527F19]" /> {t('benchmark.yourEstimate')}
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm bg-[#DDE1E5]" /> {t('benchmark.industryAvg')}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
